<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import {
  $getChangeRequestApproval,
  $getChangeRequestApprovalOptions,
  $updateChangeRequestApproval
} from '@/api/changeRequests'
import type { ChangeRequestApproval, ApprovalStatusOption, ApprovalTypeOption, UserSelect } from '@/models/changeRequest'
import { uniqBy } from 'lodash'

interface IProps {
  modelValue?: boolean,
  changeRequestApprovalId?: number | string | undefined,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  changeRequestApprovalId: undefined,
})

interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'onClose'): void
  (e: 'onUpdateApproval'): void
}

const emit = defineEmits<Emit>()

const route = useRoute()

const snackbarStore = useSnackbarStore()

const showSelectUsersDialog = ref<boolean>(false)

const changeRequestApproval = ref<ChangeRequestApproval | null>(null)

const approvalStatusOptions = ref<ApprovalStatusOption[]>([])
const approvalTypeOptions = ref<ApprovalTypeOption[]>([])

const editApprovalId = ref<number | null>(null)

const loading = ref<boolean>(false)

const getChangeRequestApproval = async () => {
  if (!props.changeRequestApprovalId) return

  loading.value = true

  try {
    const res = await $getChangeRequestApproval(props.changeRequestApprovalId)
    changeRequestApproval.value = res.data
  } catch (e: any) {
    console.log(e)
  } finally {
    loading.value = false
  }
}

const getChangeRequestApprovalOptions = async () => {
  try {
    const res = await $getChangeRequestApprovalOptions()
    approvalTypeOptions.value = res.data.actions.POST.approval_type.choices
    approvalStatusOptions.value = res.data.actions.POST.status.choices
  } catch (e: any) {
    console.log(e)
  }
}
const updateChangeRequestApproval = async (approval: ChangeRequestApproval) => {
  if (!approval || !approval.id) return

  loading.value = true

  const payload = {
    description: approval.description,
    due_date: approval.due_date,
    approval_type: approval.approval_type,
    cost_estimate_approval_amount: approval.approval_type === 2 ? approval.cost_estimate_approval_amount : 0,
    status: approval.status,
  } as ChangeRequestApproval

  const formData = new FormData()

  if (Array.isArray(approval.approvers_info)) {
    approval.approvers_info.map(i => i.id).forEach((item: any, index) => {
      formData.append('approvers', item)
    })
  }

  if (approval.files && approval.files.length) {
    for (let i = 0; i < approval.files.length; i++) {
      formData.append('files', approval.files[i])
    }
  }

  if (approval.delete_files && approval.delete_files.length) {
    for (let i = 0; i < approval.delete_files.length; i++) {
      formData.append('delete_files', approval.delete_files[i])
    }
  }

  // Add ChangeRequest fields to FormData
  for (const key in payload) {
    if (payload[key as keyof ChangeRequestApproval] !== null) {
      const value = payload[key as keyof ChangeRequestApproval]
      // If services_required
      if (Array.isArray(value)) {
        value.forEach((item: any, index) => {
          formData.append(`${key}`, item)
        })
      } else {
        formData.append(key, value as string)
      }
    }
  }

  try {
    await $updateChangeRequestApproval(String(approval.id), formData)

    changeRequestApproval.value = null
    editApprovalId.value = null

    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Approval updated successfully!'
    })
    await getChangeRequestApproval()

    emit('onUpdateApproval')
  } catch (e: any) {
    console.log(e)
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: 'Oops, something went wrong, try again'
    })
  } finally {
    loading.value = false
  }
}
const onRequestApprovalSelectUsers = (users: UserSelect[]) => {
  if (!changeRequestApproval.value) return
  const currentApprovers = changeRequestApproval.value?.approvers_info?.length ?
    changeRequestApproval.value.approvers_info : []
  // Set uniq users
  changeRequestApproval.value.approvers_info = uniqBy([ ...currentApprovers, ...users], 'id')
}
const hideDialog = () => {
  emit('update:modelValue', false)
  onClose()
}

const onClose = async () => {
  editApprovalId.value = null
  emit('onClose')
}

watch(() => props.modelValue, () => {
  // on show Dialog
  if (!!props.modelValue) {
    getChangeRequestApproval()
    getChangeRequestApprovalOptions()
  }
  // on hide Dialog
  else {
    onClose()
  }
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="800"
    :persistent="!!loading"
    @after-leave="onClose"
    @update:modelValue="emit('update:modelValue')"
  >
    <VBtn
      variant="elevated"
      density="compact"
      icon="tabler-x"
      :disabled="!!loading"
      class="v-dialog-close-btn"
      @click="hideDialog"
    />

    <VCard>
      <VCardText>
        <VOverlay
          v-model="loading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </VOverlay>

        <SelectUsersDialog
          v-model="showSelectUsersDialog"
          :dialog-title="'Approvers'"
          :user-select-label="'Approvers'"
          @on-submit="users => onRequestApprovalSelectUsers(users)"
        />

        <ChangeRequestApprovalInfo
          v-model:show-select-users-dialog="showSelectUsersDialog"
          :change-request-loading="loading"
          :approval-status-options="approvalStatusOptions"
          :approval-type-options="approvalTypeOptions"
          :request-approval="changeRequestApproval"
          :edit-approval-id="editApprovalId"
          :hide-delete-btn="true"
          @update:edit-approval-id="(val) => { editApprovalId = val }"
          @update:request-approval="(val) => !!editApprovalId && val ? changeRequestApproval = val : ''"
          @onUpdateChangeRequestApproval="updateChangeRequestApproval(changeRequestApproval)"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>
